import { useEffect } from 'react'
import s from '../DealsModal.scss'
import { Details } from './Details'
import { CompanyClients } from 'components/common/CompanyClients'
import { InternalTeam } from 'components/common/InternalTeam'
import { Tabs, KeyValuePairs } from 'components/ideal-core-ui'
import cn from 'classnames'
import { AddCompany } from './AddCompany'
import { AddContact } from './AddContact'
import update from 'immutability-helper'
import { useDispatch, useSelector } from 'react-redux'
import { toCategories, toUserRole, fromContacts, fromTeamRoles } from './serializers'
import * as ACT from '../../actions'
import { URLS } from 'utils/constants'
import { makeGetRequest, makePostRequest } from 'utils/api'
import differenceBy from 'lodash/differenceBy'
import swal from 'sweetalert'
import { useNavigate } from 'react-router-dom'
import { createSelector } from 'reselect'
import { getOrderedStatuses, utcCurrentDate } from 'utils/helpers'
import { useImmer } from 'use-immer'
import { getUser } from 'utils/authService'
import { Button } from 'reactstrap'

const loggedUser = getUser()

const initialState = {
  deal: {
    name: '',
    address: '',
    address2: '',
    street2: '',
    linkToPlans: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    sealedBid: false,
    prevailingWage: false,
    hardBid: false,
    dueDate: utcCurrentDate(),
    dealmanagerid: loggedUser
      ? {
          value: loggedUser.id,
          label: loggedUser.name
        }
      : null,
    quoteNumber: '',
    briefNote: '',
    description: '',
    teams: []
  },
  internalTeam: {
    user: '',
    role: ''
  },
  internalTeamArr: [],
  requiredFields: ['name', 'dealmanagerid', 'clients', 'dealStatus'],
  error: false,
  categories: [],
  sources: [],
  workTypes: [],
  key: 0,
  usingQuoteNumberAutomation: false
}

const orderedStatusesSelector = createSelector(
  [state => state.dealsStatuses, state => state.organizationStatuses],
  (dealsStatuses, organizationStatuses) => {
    return getOrderedStatuses(dealsStatuses, organizationStatuses)
  }
)

const DealModalContent = ({ users, createDeal, toggleAddDealModal, lead }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { dealsStatuses } = useSelector(state => state.deals)
  const { organizationStatuses } = useSelector(state => state.deals)
  const orderedStatuses = useSelector(state =>
    orderedStatusesSelector({
      dealsStatuses: state.deals.dealsStatuses,
      organizationStatuses: state.deals.organizationStatuses
    })
  )
  const [state, setState] = useImmer(initialState)

  useEffect(() => {
    if (lead) {
      setState(draft => {
        draft.deal.name = lead.name
        draft.deal.address = lead.address.address
        draft.deal.address2 = lead.address.address2
        draft.deal.city = lead.address.city
        draft.deal.state = lead.address.region
        draft.deal.zipCode = lead.address.zipCode
        draft.deal.country = lead.address.country
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead])

  useEffect(() => {
    ;(async () => {
      let response = await makeGetRequest('/v1/dealcategory')
      setState(draft => {
        draft.categories = toCategories(response)
      })
      const sources = await makeGetRequest('/v1/dealsource')
      setState(draft => {
        draft.sources = toCategories(sources)
      })

      const workTypes = await makeGetRequest('/v1/deal-work-type')
      setState(draft => {
        draft.workTypes = toCategories(workTypes)
      })

      const bidStatuses = await makeGetRequest('/v1/deal-bid-status')
      setState(draft => {
        draft.bidStatuses = toCategories(bidStatuses)
      })

      const internalBidStatuses = await makeGetRequest('/v1/deal-internal-bid-status')
      setState(draft => {
        draft.internalBidStatuses = toCategories(internalBidStatuses)
      })

      if (!dealsStatuses?.length) {
        dispatch({
          type: ACT.DEAL_STATUSES_FETCH_REQUESTED
        })
      }

      if (!organizationStatuses?.length) {
        dispatch({
          type: ACT.ORGANIZATION_STATUSES_FETCH_REQUESTED
        })
      }

      const { quoteNumberAutomation } = await makeGetRequest('/v1/deals/automation-number-status')
      setState(draft => {
        draft.usingQuoteNumberAutomation = quoteNumberAutomation
      })

      setInitialDealStatus()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, setState])

  const validateRequired = () => {
    let error = false
    setState(draft => {
      draft.error = false
    })
    state.requiredFields.forEach(field => {
      if (!state.deal[field] || (Array.isArray(state.deal[field]) && !state.deal[field].length)) {
        setState(draft => {
          draft.error = true
        })
        error = true
      }
    })
    return error
  }

  const onSave = async cb => {
    const hasErrors = validateRequired()
    const payload = lead
      ? { deal: state.deal, internalTeam: fromTeamRoles(state.internalTeamArr) }
      : state.deal

    if (!hasErrors) {
      cb(payload, async dealId => {
        if (lead) return

        if (state.internalTeamArr.length) {
          await makePostRequest(
            `/v1/deals/${dealId}/internalteam`,
            fromTeamRoles(state.internalTeamArr)
          )
        }
      })
      toggleAddDealModal?.()
    }
  }

  const incrementKey = () => {
    setState(draft => {
      draft.key = draft.key + 1
    })
  }

  const setInitialDealStatus = () => {
    setState(draft => {
      draft.deal.dealStatus = orderedStatuses[0].options[0]
    })
  }

  const onSelectCallback = params => {
    const { address, city, state, latitude, longitude, zipCode, country, street } = params
    setState(draft => {
      draft.deal = {
        ...draft.deal,
        city,
        latitude,
        longitude,
        address,
        address2: street,
        state,
        country,
        zipCode
      }
    })
  }

  const generateQuoteNumber = async () => {
    const { nextNumber } = await makeGetRequest('/v1/deals/next-quote-number')
    changeProperty(nextNumber, 'quoteNumber')
  }

  const changeProperty = (value, property) => {
    setState(draft => {
      draft.deal[property] = value
    })
  }

  const changeProperties = partialState => {
    const newDeal = { ...state.deal, ...partialState }
    setState(draft => {
      draft.deal = newDeal
    })
  }

  const changeClientProperty = (value, property, id) => {
    const index = state.deal.clients.findIndex(c => c.value === id)
    const newState = update(state, {
      deal: {
        clients: {
          [index]: {
            [property]: { $set: value }
          }
        }
      }
    })
    setState(newState)
  }

  const changeContactIsMain = (value, id, contactIndex) => {
    const clientIndex = state.deal.clients.findIndex(c => c.value === id)
    const newState = update(state, {
      deal: {
        clients: {
          [clientIndex]: {
            selectedContacts: {
              [contactIndex]: {
                isMain: { $set: value }
              }
            }
          }
        }
      }
    })
    setState(newState)
  }

  const refreshClientContacts = async id => {
    const url = `/v1/contacts/company/${id}`
    const contacts = await makeGetRequest(url)
    const index = state.deal.clients.findIndex(c => c.value === id)
    const newState = update(state, {
      deal: {
        clients: {
          [index]: {
            contacts: { $set: fromContacts(contacts) }
          }
        }
      }
    })

    setState(newState)
  }

  const changeClientContacts = async value => {
    let newState
    if (!value.length) {
      setState(draft => {
        draft.deal.clients = []
      })
      return
    }

    const { value: id, label, color } = value.slice(-1)[0]
    const url = `/v1/contacts/company/${id}`
    const contacts = await makeGetRequest(url)
    const client = {
      value: id,
      label,
      color,
      contacts: fromContacts(contacts),
      dueDate: state.deal.dueDate
    }

    if (!state.deal.clients) {
      setState(draft => {
        draft.deal.clients = [client]
      })
      return
    } else {
      if (value.length < state.deal.clients.length) {
        const removedClient = differenceBy(state.deal.clients, value, 'value')
        const index = state.deal.clients.findIndex(
          client => client.value === removedClient[0].value
        )
        newState = update(state, {
          deal: {
            clients: {
              $splice: [[index, 1]]
            }
          }
        })
      } else {
        newState = update(state, {
          deal: {
            clients: {
              $push: [client]
            }
          }
        })
      }
    }

    setState(newState)
  }

  const changeClientContactRole = (value, id, contactIndex) => {
    const clientIndex = state.deal.clients.findIndex(c => c.value === id)
    const newState = update(state, {
      deal: {
        clients: {
          [clientIndex]: {
            selectedContacts: {
              [contactIndex]: {
                role: { $set: value }
              }
            }
          }
        }
      }
    })
    setState(newState)
  }

  const showNoCompaniesAlert = async response => {
    if (!response.content.length) {
      const willNavigate = await swal({
        title: 'Please add at least one company before entering an opportunity.',
        text: 'Do you want to navigate to add a company page?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      })
      if (willNavigate) {
        navigate(URLS.AUTH_COMPANIES)
      }
    }
  }

  return (
    <Tabs
      className={s.tabs}
      noPadding
      keepState
      activeTabKey="opportunity"
      tabs={[
        {
          key: 'opportunity',
          label: 'Add New Opportunity',
          value: (
            <>
              <Button data-cy="save_btn" className={s.saveBtn} onClick={() => onSave(createDeal)}>
                {lead ? 'Save Opportunity' : 'Create Opportunity'}
              </Button>
              <hr />
              <Details
                deal={state.deal}
                users={users}
                changeProperty={changeProperty}
                changeProperties={changeProperties}
                onSelectCallback={onSelectCallback}
                categories={state.categories}
                sources={state.sources}
                orderedStatuses={orderedStatuses}
                workTypes={state.workTypes}
                bidStatuses={state.bidStatuses}
                internalBidStatuses={state.internalBidStatuses}
                usingQuoteNumberAutomation={state.usingQuoteNumberAutomation}
                generateQuoteNumber={generateQuoteNumber}
              />
              <div style={{ padding: '10px', borderBottom: '1px solid #ccc' }}>
                <InternalTeam
                  dealInternalTeam={state.internalTeamArr}
                  removeInternalTeamUser={id => {
                    const index = state.internalTeamArr.findIndex(({ user }) => user.id === id)
                    const newState = update(state, {
                      internalTeamArr: { $splice: [[index, 1]] }
                    })
                    setState(newState)
                  }}
                  changeUserCb={value => {
                    setState(draft => {
                      draft.internalTeam.user = value
                    })
                  }}
                  changeRoleCb={value => {
                    setState(draft => {
                      draft.internalTeam.role = value
                    })
                  }}
                  internalTeam={state.internalTeam}
                  users={users}
                  addInternalTeamUser={user => {
                    setState(draft => {
                      draft.internalTeamArr.push(toUserRole(user))
                      draft.internalTeam = { user: '', role: '' }
                    })
                  }}
                />
              </div>
              {state.deal.bidStatus?.value === 3 && state.deal.clients?.length > 0 && (
                <span style={{ color: 'red', position: 'relative', left: 10, top: 10 }}>
                  {state.deal.clients?.length === 1
                    ? 'This client will be set as “Client Awarded for this opportunity”'
                    : 'After you create the opportunity, make sure you set “Client Awarded” – YES on the opportunity page, and select the awarded client”'}
                </span>
              )}
              <CompanyClients
                withRoles
                defaultOptions
                defaultAction={showNoCompaniesAlert}
                changeClientContacts={changeClientContacts}
                refreshClientContacts={refreshClientContacts}
                entity={state.deal}
                changeClientProperty={changeClientProperty}
                changeContactIsMain={changeContactIsMain}
                changeClientContactRole={changeClientContactRole}
                id="company_select_cy"
                key={state.key}
              />
              {state.error && <p className={s.error}>All required fields must have values!</p>}
            </>
          )
        },
        {
          key: 'company',
          label: 'Add New Company or Contact',
          value: (
            <div className={cn(s.wrapper, s.details)}>
              <div className={s.column}>
                <KeyValuePairs
                  style={{ position: 'relative' }}
                  pairs={[
                    {
                      key: 'Add Company',
                      value: <AddCompany incrementKey={incrementKey} />
                    }
                  ]}
                />
              </div>
              <div className={s.column}>
                <KeyValuePairs
                  style={{ position: 'relative' }}
                  pairs={[
                    {
                      key: 'Add Contact',
                      value: <AddContact selectKey={state.key} incrementKey={incrementKey} />
                    }
                  ]}
                />
              </div>
            </div>
          )
        }
      ]}
    />
  )
}

export default DealModalContent
